/**
 * Design tokens — the single source of truth for colour, spacing, typography, radii
 * and elevation. `src/styles/tokens.css` (Tailwind v4 `@theme`) is GENERATED from this
 * file by `pnpm --filter @wato/ui tokens:build`; a test fails when the two drift.
 *
 * The palette is original to Wato: the Direction 1 green brand (shared with the inbox and the
 * shell rail), a saffron accent and WhatsApp-style neutrals. Light values live in `palette` +
 * `semanticColors`; dark values in `darkSemanticOverrides` (emitted under `[data-theme="dark"]`).
 */

export const palette = {
  primary: {
    50: "#eef9f5",
    100: "#dff3ec",
    200: "#bfe6d8",
    300: "#8fd2bc",
    400: "#4fb596",
    500: "#1f8f76",
    600: "#0a6b5a",
    700: "#095a4b",
    800: "#07483c",
    900: "#05372d",
    950: "#032019",
  },
  accent: {
    50: "#fff2e7",
    100: "#ffe2ce",
    200: "#fcc8a6",
    300: "#f2a772",
    400: "#da8240",
    500: "#c26300",
    600: "#a74a00",
    700: "#8e3700",
    800: "#722800",
    900: "#531800",
    950: "#350a00",
  },
  success: {
    50: "#ecf9ee",
    100: "#d8f1dc",
    200: "#b7e1be",
    300: "#8dcd99",
    400: "#62b074",
    500: "#3b9555",
    600: "#1d7d3e",
    700: "#01672d",
    800: "#00521f",
    900: "#003a12",
    950: "#002306",
  },
  warning: {
    50: "#fef3e5",
    100: "#fce6c9",
    200: "#f2ce9d",
    300: "#e5b064",
    400: "#cb8d23",
    500: "#b37000",
    600: "#995700",
    700: "#814400",
    800: "#683300",
    900: "#4b2200",
    950: "#2f1100",
  },
  danger: {
    50: "#ffeeec",
    100: "#ffdcd6",
    200: "#ffbcb5",
    300: "#ff948c",
    400: "#f36963",
    500: "#db4241",
    600: "#be222a",
    700: "#a20419",
    800: "#83000c",
    900: "#600004",
    950: "#3f0000",
  },
  info: {
    50: "#e9f8ff",
    100: "#d1eeff",
    200: "#abdcf9",
    300: "#79c5f0",
    400: "#45a6d9",
    500: "#008bc2",
    600: "#0072a8",
    700: "#005d8f",
    800: "#004974",
    900: "#003354",
    950: "#001e36",
  },
  neutral: {
    0: "#ffffff",
    25: "#f7faf9",
    50: "#f4f7f6",
    100: "#ebf0ee",
    200: "#e3e9e7",
    300: "#b6bec5",
    400: "#98a4ad",
    500: "#738591",
    600: "#5a6b75",
    700: "#54656f",
    800: "#3b4a54",
    900: "#233138",
    950: "#111b21",
  },
} as const;

export type PaletteName = keyof typeof palette;
export type PaletteRef = {
  [P in PaletteName]: `${P}.${keyof (typeof palette)[P] & number}`;
}[PaletteName];

/**
 * Semantic colours — what components use. Each maps to a palette step; names become
 * Tailwind utilities (`bg-surface`, `text-fg-muted`, `border-line-strong`, …).
 */
export const semanticColors = {
  // Surfaces
  bg: "neutral.50",
  surface: "neutral.0",
  "surface-muted": "neutral.25",
  "surface-sunken": "neutral.100",
  // Text
  fg: "neutral.950",
  "fg-muted": "neutral.700",
  "fg-subtle": "neutral.600",
  // Lines
  line: "neutral.200",
  "line-strong": "neutral.500",
  focus: "primary.600",
  // Actions (Button)
  "action-primary": "primary.600",
  "action-primary-hover": "primary.700",
  "on-action-primary": "neutral.0",
  "action-secondary": "neutral.0",
  "action-secondary-hover": "neutral.100",
  "on-action-secondary": "neutral.950",
  "action-danger": "danger.600",
  "action-danger-hover": "danger.700",
  "on-action-danger": "neutral.0",
  "action-ghost-hover": "primary.50",
  "on-action-ghost": "primary.700",
  // Status text + tinted backgrounds (Input errors, Toast, badges)
  "danger-fg": "danger.700",
  "danger-line": "danger.600",
  "success-bg": "success.50",
  "success-fg": "success.900",
  "success-icon": "success.700",
  "danger-bg": "danger.50",
  "danger-bg-fg": "danger.900",
  "danger-icon": "danger.700",
  "info-bg": "info.50",
  "info-fg": "info.900",
  "info-icon": "info.700",
  "warning-bg": "warning.50",
  "warning-fg": "warning.900",
  "warning-icon": "warning.700",
  "accent-fg": "accent.700",
  "accent-soft": "primary.100",
} as const satisfies Record<string, PaletteRef>;

export type SemanticColor = keyof typeof semanticColors;

/**
 * WCAG 2.1 AA pairs on the default palette (checked by test/contrast.test.ts).
 * 4.5:1 for text (1.4.3), 3:1 for UI component boundaries and focus indicators (1.4.11).
 */
export const contrastRequirements: ReadonlyArray<{
  readonly fg: SemanticColor;
  readonly bg: SemanticColor;
  readonly min: 4.5 | 3;
  readonly usage: string;
}> = [
  { fg: "fg", bg: "surface", min: 4.5, usage: "body text" },
  { fg: "fg", bg: "surface-muted", min: 4.5, usage: "body text on muted panels" },
  { fg: "fg-muted", bg: "surface", min: 4.5, usage: "secondary text" },
  { fg: "fg-muted", bg: "surface-muted", min: 4.5, usage: "secondary text on muted panels" },
  { fg: "fg-subtle", bg: "surface", min: 4.5, usage: "Input placeholder" },
  { fg: "line-strong", bg: "surface", min: 3, usage: "Input border" },
  { fg: "danger-line", bg: "surface", min: 3, usage: "Input error border" },
  { fg: "focus", bg: "surface", min: 3, usage: "focus ring" },
  { fg: "focus", bg: "surface-muted", min: 3, usage: "focus ring on muted panels" },
  { fg: "danger-fg", bg: "surface", min: 4.5, usage: "Input error text" },
  { fg: "accent-fg", bg: "surface", min: 4.5, usage: "accent text" },
  { fg: "success-fg", bg: "success-bg", min: 4.5, usage: "success Toast" },
  { fg: "success-icon", bg: "success-bg", min: 3, usage: "success Toast icon" },
  { fg: "danger-bg-fg", bg: "danger-bg", min: 4.5, usage: "error Toast" },
  { fg: "danger-icon", bg: "danger-bg", min: 3, usage: "error Toast icon" },
  { fg: "info-fg", bg: "info-bg", min: 4.5, usage: "info Toast" },
  { fg: "info-icon", bg: "info-bg", min: 3, usage: "info Toast icon" },
  { fg: "warning-fg", bg: "warning-bg", min: 4.5, usage: "warning banner" },
  { fg: "fg-subtle", bg: "surface-sunken", min: 4.5, usage: "placeholder on sunken surface" },
  { fg: "on-action-primary", bg: "action-primary", min: 4.5, usage: "primary button" },
  { fg: "on-action-primary", bg: "action-primary-hover", min: 4.5, usage: "primary button hover" },
  {
    fg: "on-action-secondary",
    bg: "action-secondary-hover",
    min: 4.5,
    usage: "secondary button hover",
  },
  { fg: "on-action-danger", bg: "action-danger", min: 4.5, usage: "danger button" },
  { fg: "on-action-danger", bg: "action-danger-hover", min: 4.5, usage: "danger button hover" },
  { fg: "on-action-ghost", bg: "surface", min: 4.5, usage: "ghost button / link text" },
  { fg: "on-action-ghost", bg: "action-ghost-hover", min: 4.5, usage: "ghost button hover" },
  { fg: "action-primary", bg: "surface", min: 3, usage: "primary action boundary" },
];

/** Dark value of a semantic token (override, else the light palette value). */
export function resolveSemanticColorDark(name: SemanticColor): string {
  return (
    (darkSemanticOverrides as Partial<Record<SemanticColor, string>>)[name] ??
    resolveSemanticColor(name)
  );
}

/**
 * Inbox (Direction 1, compact WhatsApp-Web style) surface tokens. Each has a light and a dark value;
 * emitted as `--color-app-*` (Tailwind `bg-app-thread`, `text-app-fg`...; legacy `--color-inbox-*` aliases kept) with the dark value
 * under `[data-theme="dark"]`. Channel colours: QR amber, Official green, Instagram magenta, group indigo.
 */
export const appColors = {
  "app-bg": { light: "#f4f7f6", dark: "#0b141a" },
  "app-surface": { light: "#ffffff", dark: "#111b21" },
  "app-surface-2": { light: "#f0f4f2", dark: "#1f2c34" },
  "app-line": { light: "#e3e9e7", dark: "#2a3942" },
  "app-fg": { light: "#111b21", dark: "#e9edef" },
  "app-muted": { light: "#54656f", dark: "#9db0ba" },
  "app-accent": { light: "#0a6b5a", dark: "#3ecf8e" },
  "app-accent-soft": { light: "#dff3ec", dark: "#12372d" },
  "app-accent-fg": { light: "#0a5a4b", dark: "#3ecf8e" },
  "app-on-accent": { light: "#ffffff", dark: "#04251a" },
  "app-rail": { light: "#0a3d34", dark: "#0d1a1f" },
  "app-rail-fg": { light: "#eaf6f2", dark: "#eaf6f2" },
  "app-rail-muted": { light: "#b6d6cc", dark: "#b6d6cc" },
  "app-rail-active": { light: "#0b7662", dark: "#3ecf8e" },
  "app-on-rail-active": { light: "#ffffff", dark: "#04251a" },
  "app-logo": { light: "#25d366", dark: "#25d366" },
  "app-on-logo": { light: "#04251a", dark: "#04251a" },
  "app-in": { light: "#ffffff", dark: "#202c33" },
  "app-out": { light: "#d9fdd3", dark: "#005c4b" },
  "app-out-muted": { light: "#4a5b65", dark: "#c8e0d8" },
  "app-out-accent": { light: "#0a5a4b", dark: "#8ff0c4" },
  "app-thread": { light: "#efeae2", dark: "#0b141a" },
  "app-note-bg": { light: "#fff3c4", dark: "#3a3000" },
  "app-note-fg": { light: "#5a4300", dark: "#ffe8a3" },
  "app-ch-qr": { light: "#9a5b00", dark: "#9a5b00" },
  "app-ch-off": { light: "#0a7a4a", dark: "#0a7a4a" },
  "app-ch-ig": { light: "#b0245f", dark: "#b0245f" },
  "app-ch-grp": { light: "#4f46a5", dark: "#4f46a5" },
  "app-ch-qr-fg": { light: "#9a5b00", dark: "#f0b45a" },
  "app-ch-off-fg": { light: "#0a7a4a", dark: "#5fe0a6" },
  "app-ch-ig-fg": { light: "#b0245f", dark: "#ff8fbd" },
  "app-ch-grp-fg": { light: "#4f46a5", dark: "#b4aeff" },
} as const satisfies Record<string, { light: string; dark: string }>;

export type AppColor = keyof typeof appColors;

/**
 * Dark-theme values for the semantic tokens (surfaces, text, lines, actions, status), so every page
 * follows `[data-theme="dark"]`. Hex (not palette refs): the dark scale is not in the palette.
 */
export const darkSemanticOverrides = {
  bg: "#0b141a",
  surface: "#111b21",
  "surface-muted": "#17232a",
  "surface-sunken": "#1f2c34",
  fg: "#e9edef",
  "fg-muted": "#b4c3cb",
  "fg-subtle": "#9db0ba",
  line: "#2a3942",
  "line-strong": "#7f929c",
  "action-secondary": "#111b21",
  "action-secondary-hover": "#1f2c34",
  "on-action-secondary": "#e9edef",
  focus: "#6db3ff",
  "action-ghost-hover": "#1f2c34",
  "on-action-ghost": "#3ecf8e",
  "action-primary": "#3ecf8e",
  "action-primary-hover": "#5fe0a6",
  "on-action-primary": "#04251a",
  "accent-soft": "#12372d",
  "accent-fg": "#f0b45a",
  "danger-fg": "#ffb4ab",
  "danger-line": "#ff8f86",
  "success-bg": "#12372d",
  "success-fg": "#8ff0c4",
  "success-icon": "#5fe0a6",
  "danger-bg": "#3a1512",
  "danger-bg-fg": "#ffb4ab",
  "danger-icon": "#ff8f86",
  "info-bg": "#14283f",
  "info-fg": "#a9c9f5",
  "info-icon": "#7fb2f0",
  "warning-bg": "#3a3000",
  "warning-fg": "#ffe8a3",
  "warning-icon": "#f0b45a",
} as const satisfies Partial<Record<SemanticColor, string>>;

/** 4 px base grid: Tailwind's `p-1` = 1 × unit, `gap-4` = 4 × unit = 16 px. */
export const spacing = {
  unit: "0.25rem",
} as const;

/** Spacing steps allowed for layout primitives (multiples of the 4 px unit). */
export const spaceScale = [0, 1, 2, 3, 4, 5, 6, 8, 10, 12, 16] as const;
export type Space = (typeof spaceScale)[number];

export const radii = {
  sm: "0.25rem",
  md: "0.5rem",
  lg: "0.75rem",
  xl: "0.875rem",
  "2xl": "1.375rem",
  full: "9999px",
} as const;

/** Elevation: shadow layers tinted with neutral.950. */
export const elevation = {
  sm: [{ y: 1, blur: 2, spread: 0, alpha: 0.08 }],
  md: [
    { y: 2, blur: 6, spread: -1, alpha: 0.1 },
    { y: 1, blur: 3, spread: 0, alpha: 0.06 },
  ],
  lg: [
    { y: 12, blur: 28, spread: -6, alpha: 0.18 },
    { y: 4, blur: 10, spread: -4, alpha: 0.1 },
  ],
} as const;

/**
 * Font pairing (self-hosted via @fontsource): Inter for Latin, IBM Plex Sans Arabic for
 * Arabic. Inter has no Arabic glyphs, so Arabic runs fall through to Plex automatically
 * in either UI language, and mixed AR/EN strings render with both faces.
 */
export const fonts = {
  sans: ['"Inter Variable"', '"IBM Plex Sans Arabic"', "ui-sans-serif", "system-ui", "sans-serif"],
  mono: ["ui-monospace", "SFMono-Regular", "Menlo", "Consolas", "monospace"],
} as const;

export const fontWeights = { normal: 400, medium: 500, semibold: 600, bold: 700 } as const;

/**
 * Type scale (rem). Arabic script has taller ascenders/descenders and diacritics, so the
 * line-height is raised under `:lang(ar)` without changing font sizes.
 */
export const typeScale = {
  xs: { size: "0.75rem", lineHeight: "1rem", lineHeightArabic: "1.25rem" },
  sm: { size: "0.875rem", lineHeight: "1.25rem", lineHeightArabic: "1.5rem" },
  base: { size: "1rem", lineHeight: "1.5rem", lineHeightArabic: "1.75rem" },
  lg: { size: "1.125rem", lineHeight: "1.75rem", lineHeightArabic: "2rem" },
  xl: { size: "1.25rem", lineHeight: "1.75rem", lineHeightArabic: "2.25rem" },
  "2xl": { size: "1.5rem", lineHeight: "2rem", lineHeightArabic: "2.5rem" },
  "3xl": { size: "1.875rem", lineHeight: "2.5rem", lineHeightArabic: "2.75rem" },
} as const;

export const breakpoints = { sm: "40rem", md: "48rem", lg: "64rem", xl: "80rem" } as const;

/** Named layout sizes (exposed as `--size-*`). */
export const sizes = {
  sidebar: "16rem",
  rail: "4.75rem",
  dialog: "32rem",
  toast: "24rem",
  content: "72rem",
  "content-wide": "100rem",
} as const;

/** Resolves a palette reference (`"primary.600"`) to its hex value. */
export function resolvePaletteRef(ref: PaletteRef): string {
  const [name, step] = ref.split(".") as [PaletteName, string];
  const scale = palette[name] as Readonly<Record<string, string>>;
  const hex = scale[step];
  if (hex === undefined) throw new Error(`Unknown palette ref ${ref}`);
  return hex;
}

export function resolveSemanticColor(name: SemanticColor): string {
  return resolvePaletteRef(semanticColors[name]);
}

/** Typed token object (also importable by apps for charts, canvas, etc.). */
export const tokens = {
  palette,
  semanticColors,
  spacing,
  spaceScale,
  radii,
  elevation,
  fonts,
  fontWeights,
  typeScale,
  breakpoints,
  sizes,
} as const;

export type Tokens = typeof tokens;
